import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LS_CHAT = "ukpr_chat_id";
const LS_NAME = "ukpr_chat_name";
const POLL_MS = 3500;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [chatId, setChatId] = useState(() => localStorage.getItem(LS_CHAT) || "");
  const [name, setName] = useState(() => localStorage.getItem(LS_NAME) || "");
  const [email, setEmail] = useState("");
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [adminOnline, setAdminOnline] = useState(false);
  const [starting, setStarting] = useState(false);
  const scrollRef = useRef(null);

  // Poll presence (always), and chat (only when chat exists and widget is open)
  useEffect(() => {
    let active = true;
    const presence = () => api.get("/chat/presence").then((r) => active && setAdminOnline(r.data.admin_online)).catch(() => {});
    presence();
    const t = setInterval(presence, 15000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!chatId || !open) return;
    let active = true;
    const poll = () => api.get(`/chats/${chatId}`).then((r) => {
      if (!active) return;
      setMessages(r.data.messages || []);
      setAdminOnline(r.data.admin_online);
    }).catch(() => {});
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => { active = false; clearInterval(t); };
  }, [chatId, open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const startChat = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setStarting(true);
    try {
      const { data } = await api.post("/chats/start", { name: name.trim(), email: email.trim() });
      localStorage.setItem(LS_CHAT, data.chat_id);
      localStorage.setItem(LS_NAME, name.trim());
      setChatId(data.chat_id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStarting(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !chatId) return;
    const text = body.trim();
    setBody("");
    const optimistic = { id: `tmp-${Date.now()}`, sender: "visitor", body: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    try {
      await api.post(`/chats/${chatId}/messages`, { body: text });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const endChat = () => {
    localStorage.removeItem(LS_CHAT);
    setChatId("");
    setMessages([]);
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 px-4 py-3 bg-[#ff1493] text-white border-[3px] border-black shadow-brut btn-press font-mono-ui text-xs font-bold"
        >
          <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
          CHAT WITH US
          <span className={`w-2 h-2 ${adminOnline ? "bg-[#22c55e]" : "bg-white/60"} border border-black`} />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-50 w-[92vw] sm:w-[380px] h-[560px] max-h-[85vh] flex flex-col bg-white border-[3px] border-black shadow-brut"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b-[3px] border-black bg-black text-white">
            <div>
              <div className="font-display text-base">UK PEPTIDE RESEARCH</div>
              <div className="font-mono-ui text-[10px] flex items-center gap-1.5">
                <span className={`w-2 h-2 ${adminOnline ? "bg-[#22c55e]" : "bg-[#facc15]"}`} />
                {adminOnline ? "ONLINE NOW · WE REPLY IN MINUTES" : "AWAY · WE'LL EMAIL YOU BACK"}
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="w-8 h-8 border-[2px] border-white flex items-center justify-center hover:bg-[#ff1493]">
              <X className="w-4 h-4" />
            </button>
          </div>

          {!chatId ? (
            <form onSubmit={startChat} className="flex-1 p-5 flex flex-col gap-3">
              <div className="font-display text-xl">Start a chat</div>
              <p className="font-mono-ui text-[11px] text-black/70 leading-relaxed">
                Leave your name &amp; email so we can reply if you drop off.
                All questions welcome &mdash; strictly research-grade.
              </p>
              <input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="chat-start-name"
                required
                className="border-[3px] border-black px-3 py-2 font-mono-ui text-xs"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="chat-start-email"
                required
                className="border-[3px] border-black px-3 py-2 font-mono-ui text-xs"
              />
              <button
                type="submit"
                disabled={starting}
                data-testid="chat-start-submit"
                className="mt-1 px-4 py-3 bg-[#ff1493] text-white border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-xs font-bold disabled:opacity-60"
              >
                {starting ? "STARTING…" : "START CHAT"}
              </button>
              <div className="mt-auto font-mono-ui text-[10px] text-black/50">
                We never share your email. FOR RESEARCH USE ONLY.
              </div>
            </form>
          ) : (
            <>
              <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f4f4f5]">
                {messages.length === 0 && (
                  <div className="font-mono-ui text-[11px] text-black/50 text-center py-6">
                    SAY HI 👋 — WE'LL BE WITH YOU SHORTLY.
                  </div>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] border-[3px] border-black px-3 py-2 text-sm leading-relaxed shadow-brut-sm ${
                        m.sender === "visitor" ? "bg-[#ff1493] text-white" : "bg-white text-black"
                      }`}
                    >
                      <div className="font-mono-ui text-[9px] opacity-70 mb-0.5">
                        {m.sender === "visitor" ? "YOU" : "UKPR"}
                      </div>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="border-t-[3px] border-black p-3 flex items-center gap-2">
                <input
                  placeholder="Type a message…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="chat-input"
                  className="flex-1 border-[3px] border-black px-3 py-2 font-mono-ui text-xs"
                />
                <button
                  type="submit"
                  disabled={!body.trim()}
                  data-testid="chat-send"
                  className="w-10 h-10 bg-[#ff1493] text-white border-[3px] border-black shadow-brut-sm btn-press flex items-center justify-center disabled:opacity-60"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <div className="px-3 pb-2 font-mono-ui text-[9px] text-black/40 flex items-center justify-between">
                <span>CHAT ID {chatId.slice(0, 8).toUpperCase()}</span>
                <button data-testid="chat-end" onClick={endChat} className="underline hover:text-black">END CHAT</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
